import { useState, useEffect, useCallback, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import "tailwindcss/tailwind.css";
import { getUser, getVooruitgang } from "../../Localstorage/localstorage";
import moment from "moment";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { useSelector } from "react-redux";
import { vooruitgangTabs } from "../../function";
import { AXIOS_GET } from "../../server/api/crud";
import SelectPatienten from "../../Componenten/SelectPatienten";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
);

const calculateAverage = (data) => {
  const total = data?.reduce((sum, item) => sum + item.score, 0) || 0;
  return data?.length ? parseFloat((total / data.length).toFixed(2)) : 0;
};

const defaultThemeColors = {
  grid: "rgba(42, 57, 66, 0.65)",
  tick: "rgb(134, 150, 160)",
  legend: "rgb(233, 237, 239)",
  line: "rgb(0, 168, 132)",
  fill: "rgba(0, 168, 132, 0.22)",
  tooltipBg: "rgb(32, 44, 51)",
  tooltipBorder: "rgb(42, 57, 66)",
};

const normalizeRgbParts = (spaceSeparatedRgb) => {
  if (!spaceSeparatedRgb || typeof spaceSeparatedRgb !== "string") {
    return null;
  }

  const parts = spaceSeparatedRgb
    .trim()
    .split(/\s+/)
    .map((part) => Number(part))
    .filter((part) => Number.isFinite(part));

  return parts.length >= 3 ? parts.slice(0, 3) : null;
};

const toRgb = (spaceSeparatedRgb, fallback) => {
  const parts = normalizeRgbParts(spaceSeparatedRgb);
  if (!parts) {
    return fallback;
  }
  return `rgb(${parts.join(", ")})`;
};

const toRgba = (spaceSeparatedRgb, alpha, fallback) => {
  const parts = normalizeRgbParts(spaceSeparatedRgb);
  if (!parts) {
    return fallback;
  }
  return `rgba(${parts.join(", ")}, ${alpha})`;
};

const readThemeColors = () => {
  if (typeof window === "undefined") {
    return defaultThemeColors;
  }

  const styles = window.getComputedStyle(document.documentElement);
  const border = styles.getPropertyValue("--wa-border");
  const muted = styles.getPropertyValue("--wa-muted");
  const text = styles.getPropertyValue("--wa-text");
  const accent = styles.getPropertyValue("--wa-accent");
  const panel = styles.getPropertyValue("--wa-panel");
  const isDark = document.documentElement.classList.contains("dark");
  const lineColor = isDark ? toRgb(accent, defaultThemeColors.line) : "rgb(196, 181, 253)";

  return {
    grid: toRgba(border, 0.65, defaultThemeColors.grid),
    tick: toRgb(muted, defaultThemeColors.tick),
    legend: toRgb(text, defaultThemeColors.legend),
    line: lineColor,
    fill: isDark ? toRgba(accent, 0.22, defaultThemeColors.fill) : "rgba(196, 181, 253, 0.26)",
    tooltipBg: toRgb(panel, defaultThemeColors.tooltipBg),
    tooltipBorder: toRgb(border, defaultThemeColors.tooltipBorder),
  };
};

const EvolutionAdmin = () => {
  const user = getUser();
  const [metricData, setMetricData] = useState({
    slaap: [],
    energie: [],
    stress: [],
    cravings: [],
  });

  const { patienten } = useSelector((state) => state.user);
  const vooruitgang = getVooruitgang();
  const tabs = vooruitgangTabs(user, vooruitgang);
  const [selectedMetric, setSelectedMetric] = useState(tabs[0]);
  const [averageScore, setAverageScore] = useState({
    slaap: 0,
    energie: 0,
    stress: 0,
    cravings: 0,
  });
  const [selected, setSelected] = useState(patienten[0]);
  const [themeColors, setThemeColors] = useState(defaultThemeColors);

  const fetchVooruitgangData = useCallback(
    async (userId) => {
      try {
        const response = await AXIOS_GET(`vooruitgang/uservooruitgang/${userId}`);

        if (response.status !== 201) {
          console.warn("Unexpected response status:", response.status);
          return;
        }

        const {
          slaapDatas = [],
          energieDatas = [],
          stressDatas = [],
          cravingsDatas = [],
        } = response.data;

        const formatData = (data) =>
          data?.map((item) => ({
            ...item,
            date: moment(item.date).format("YYYY-MM-DD"),
          })) || [];

        const dataVooruitgang = {
          slaap: formatData(slaapDatas),
          energie: formatData(energieDatas),
          stress: formatData(stressDatas),
          cravings: formatData(cravingsDatas),
        };

        const averageScores = {
          slaap: calculateAverage(slaapDatas),
          energie: calculateAverage(energieDatas),
          stress: calculateAverage(stressDatas),
          cravings: calculateAverage(cravingsDatas),
        };

        setMetricData(dataVooruitgang);
        setAverageScore(averageScores);
      } catch (error) {
        console.error("Error fetching data (vooruitgang):", error);
      }
    },
    []
  );

  useEffect(() => {
    const gebruiker = selected?.user_id;
    if (gebruiker) fetchVooruitgangData(gebruiker);
  }, [selected, fetchVooruitgangData]);

  useEffect(() => {
    const syncThemeColors = () => setThemeColors(readThemeColors());
    syncThemeColors();

    const observer = new MutationObserver(syncThemeColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    window.addEventListener("storage", syncThemeColors);
    return () => {
      observer.disconnect();
      window.removeEventListener("storage", syncThemeColors);
    };
  }, []);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      color: themeColors.tick,
      scales: {
        x: {
          ticks: { color: themeColors.tick, maxRotation: 45, minRotation: 0 },
          grid: { color: themeColors.grid },
          border: { color: themeColors.grid },
        },
        y: {
          ticks: { color: themeColors.tick },
          grid: { color: themeColors.grid },
          border: { color: themeColors.grid },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: themeColors.legend,
            usePointStyle: true,
            pointStyle: "rectRounded",
          },
        },
        tooltip: {
          callbacks: {
            title: function (tooltipItems) {
              return `${tooltipItems[0].label}`;
            },
            label: function (tooltipItem) {
              const index = tooltipItem?.dataIndex;
              const rawData = metricData[selectedMetric.key][index];
              return `Score: ${tooltipItem?.raw} ${
                rawData?.uitleg ? " | Meer uitleg : " + rawData?.uitleg : ""
              }`;
            },
            footer: function (tooltipItems) {
              return `Extra Info: ${tooltipItems.length} points`;
            },
          },
          enabled: false,
          external: function (context) {
            let tooltipEl = document.getElementById("chartjs-tooltip-vooruitgang-admin");
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = "chartjs-tooltip-vooruitgang-admin";
              tooltipEl.style.position = "absolute";
              tooltipEl.style.padding = "0px";
              tooltipEl.style.pointerEvents = "none";
              tooltipEl.style.transition = "all 0.2s ease";
              tooltipEl.style.zIndex = "50";
              document.body.appendChild(tooltipEl);
            }

            const tooltipModel = context?.tooltip;

            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = "0";
              return;
            }

            if (tooltipModel?.body) {
              const index = tooltipModel?.dataPoints[0]?.dataIndex;
              const rawData = metricData[selectedMetric.key][index];

              let innerHtml = `
            <div class="text-xs"><strong style="color:${themeColors.legend}">Datum:</strong> <span style="color:${themeColors.legend}">${tooltipModel?.title[0]}</span></div>
            <div class="text-xs flex items-center gap-x-1" style="margin-top:4px">
              <div style="width:10px;height:10px;border-radius:3px;background:${themeColors.line}"></div>
              <strong style="color:${themeColors.legend}">Score:</strong> <span style="color:${themeColors.legend}">${tooltipModel?.dataPoints[0].raw}</span>
            </div>`;

              if (rawData?.uitleg) {
                innerHtml += `
            <div class="text-xs mt-2" style="color:${themeColors.tick}"><strong style="color:${themeColors.legend}">Meer info:</strong></div>
            <div class="text-xs" style="color:${themeColors.legend}">${rawData?.uitleg}</div>`;
              }

              tooltipEl.innerHTML = sanitizeHtml(
                `<div class="max-w-56 rounded-lg border p-3 shadow-lg" style="background:${themeColors.tooltipBg};border-color:${themeColors.tooltipBorder}">` +
                  innerHtml +
                  "</div>"
              );
            }

            const canvas = context.chart.canvas;
            const position = canvas.getBoundingClientRect();

            tooltipEl.style.opacity = "1";
            tooltipEl.style.left =
              position.left +
              window.pageXOffset +
              tooltipModel.caretX -
              90 +
              "px";
            tooltipEl.style.top =
              position.top + window.pageYOffset + tooltipModel.caretY + "px";
          },
        },
      },
    }),
    [metricData, selectedMetric.key, themeColors]
  );

  const lineData = useMemo(
    () => ({
      labels: metricData[selectedMetric.key]?.map(
        (item) => `Dag ${moment(item.date).format("L")}`
      ),
      datasets: [
        {
          label: `${
            selectedMetric?.key?.charAt(0)?.toUpperCase() +
            selectedMetric?.key?.slice(1)
          } Niveau`,
          data: metricData[selectedMetric.key]?.map((item) => item.score),
          borderColor: themeColors.line,
          backgroundColor: themeColors.fill,
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: themeColors.line,
          pointBorderColor: themeColors.tooltipBg,
          borderWidth: 2,
        },
      ],
    }),
    [metricData, selectedMetric, themeColors]
  );

  const score = averageScore[selectedMetric?.key];
  const scorePositive = score > 5;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-wa-app px-4 py-5 text-wa-text sm:px-6">
      <h2 className="text-2xl font-semibold tracking-tight text-wa-text">Vooruitgang</h2>

      <div className="mt-6 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
        <div className="w-full min-w-0 lg:max-w-md">
          {patienten?.length > 0 && (
            <SelectPatienten
              variant="wa"
              selected={selected || patienten[0]}
              setSelected={setSelected}
              data={patienten}
            />
          )}
        </div>
        <div
          className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-xl border border-wa-border bg-wa-panel p-1"
          role="tablist"
          aria-label="Metriek"
        >
          {tabs.map((tab) => {
            const active = selectedMetric.key === tab?.key;
            return (
              <button
                key={tab?.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-wa-accent text-white shadow-sm"
                    : "text-wa-muted hover:bg-wa-hover hover:text-wa-text"
                }`}
                onClick={() => setSelectedMetric(tab)}
              >
                {tab?.naam}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 min-h-0 flex-1">
        <div className="h-[min(24rem,50vh)] min-h-[280px] rounded-xl border border-wa-border bg-wa-panel p-4 shadow-lg shadow-black/20 sm:h-96">
          <Line data={lineData} options={chartOptions} />
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-wa-border bg-wa-panel p-5 shadow-lg shadow-black/20">
        <h3 className="text-lg font-semibold text-wa-text">
          {tabs.find((tab) => tab.key === selectedMetric.key)?.naam}
        </h3>
        <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-wa-muted">
          <span>Gemiddelde score:</span>
          <span
            className={`inline-flex min-w-[2.5rem] items-center justify-center rounded-full px-3 py-1 text-sm font-semibold ${
              scorePositive
                ? "bg-wa-accent/20 text-wa-text"
                : "bg-red-900/60 text-red-100"
            }`}
          >
            {score}
          </span>
        </p>
        <p className="mt-3 text-xs leading-relaxed text-wa-muted">
          {selectedMetric.key === "slaap"
            ? "0 = Slechte nachtrust, 10 = Geslapen als een roos"
            : selectedMetric.key === "energie"
            ? "0 = Geen energie, 10 = Zeer energiek"
            : selectedMetric.key === "stress"
            ? "0 = Geen stress, 10 = Extreme stress"
            : "0 = Goede verzadiging, 10 = Continue cravings"}
        </p>
      </div>
    </div>
  );
};

export default EvolutionAdmin;
